import { API_BASE_URL, getAuthToken } from './api';
import { supabase } from '@/supabaseClient';

// === Helper ===

// Last successfully-resolved auth token, so unload handlers can fire a
// synchronous keepalive release without awaiting getAuthToken().
let cachedAuthToken: string | null = null;

async function authFetch(url: string, options: RequestInit = {}): Promise<Response> {
  const attempt = async (): Promise<Response> => {
    const token = await getAuthToken();
    if (!token) throw new Error('Not authenticated');
    cachedAuthToken = token;
    return fetch(url, {
      ...options,
      headers: {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${token}`,
        ...options.headers,
      },
    });
  };

  let response = await attempt();
  if (response.status === 401) {
    const { error } = await supabase.auth.refreshSession();
    if (!error) response = await attempt();
  }

  if (!response.ok) {
    const error = await response.json().catch(() => ({ detail: response.statusText }));
    throw new Error(error.detail || `Request failed: ${response.status}`);
  }
  return response;
}

// === Types ===

export interface VMOSDevice {
  id: string;
  pad_id: string;
  device_name: string;
  device_group: string | null;
  platform: string;
  username: string | null;
  screen_width: number;
  screen_height: number;
  status: string;
  last_used: string | null;
  created_at: string | null;
  // Live device-lock state (a run is currently using this device).
  busy?: boolean;
  locked_by?: string | null;
  busy_kind?: string | null;
  // Structured platforms + device prompt profile (saved prompts/persona).
  platforms?: string[];
  prompt_profile?: {
    folder?: string;
    persona_id?: string | null;
    templates?: Array<{
      template_id: string;
      engagement?: string | null;
      platform?: string | null;
      no_search?: boolean;
      surface?: string | null;
    }>;
  };
}

export interface DevicesResponse {
  devices: VMOSDevice[];
  sync_group: string[];
  master_pad_id: string | null;
}

export interface TouchAction {
  x: number;
  y: number;
  action_type?: string;
  duration?: number;
  end_x?: number;
  end_y?: number;
}

export interface TikTokAction {
  action: string;
  text?: string;
}

export interface AgentStartRequest {
  task: string;
  max_steps?: number;
}

export interface AgentResponse {
  agent_id: string;
  task: string;
  status: string;
  max_steps: number;
}

// === API Functions ===

const BASE = `${API_BASE_URL}/vmos-group-control`;

export async function fetchDevices(): Promise<DevicesResponse> {
  const res = await authFetch(`${BASE}/devices`);
  return res.json();
}

export function getScreenshotUrl(padId: string, quality: number = 70): string {
  // Returns the URL - callers will fetch with auth header
  return `${BASE}/devices/${padId}/screenshot?quality=${quality}&t=${Date.now()}`;
}

export async function setSyncGroup(padIds: string[]): Promise<any> {
  const res = await authFetch(`${BASE}/sync-group`, {
    method: 'POST',
    body: JSON.stringify({ pad_ids: padIds }),
  });
  return res.json();
}

export async function setMasterDevice(padId: string): Promise<any> {
  const res = await authFetch(`${BASE}/master`, {
    method: 'POST',
    body: JSON.stringify({ pad_id: padId }),
  });
  return res.json();
}

export async function sendTouch(action: TouchAction): Promise<any> {
  const res = await authFetch(`${BASE}/touch`, {
    method: 'POST',
    body: JSON.stringify(action),
  });
  return res.json();
}

export async function sendText(text: string): Promise<any> {
  const res = await authFetch(`${BASE}/text`, {
    method: 'POST',
    body: JSON.stringify({ text }),
  });
  return res.json();
}

export async function sendKey(keycode: number): Promise<any> {
  const res = await authFetch(`${BASE}/key`, {
    method: 'POST',
    body: JSON.stringify({ keycode }),
  });
  return res.json();
}

export async function executeTikTokAction(action: TikTokAction): Promise<any> {
  const res = await authFetch(`${BASE}/tiktok-action`, {
    method: 'POST',
    body: JSON.stringify(action),
  });
  return res.json();
}

export async function startAgent(request: AgentStartRequest): Promise<AgentResponse> {
  const res = await authFetch(`${BASE}/agent/start`, {
    method: 'POST',
    body: JSON.stringify(request),
  });
  return res.json();
}

export async function stopAgent(agentId: string): Promise<any> {
  const res = await authFetch(`${BASE}/agent/${agentId}/stop`, {
    method: 'POST',
  });
  return res.json();
}

export function getAgentStreamUrl(agentId: string): string {
  return `${BASE}/agent/${agentId}/stream`;
}

export interface UploadFileRequest {
  file: File;
  padIds: string[];
  destinationPath?: string;
  autoInstall?: boolean;
  packageName?: string;
}

export interface UploadFileResponse {
  r2_key: string;
  file_name: string;
  md5: string;
  pad_ids: string[];
  vmos_result: any;
}

export async function uploadFileToDevices(req: UploadFileRequest): Promise<UploadFileResponse> {
  const token = await getAuthToken();
  if (!token) throw new Error('Not authenticated');

  const formData = new FormData();
  formData.append('file', req.file);
  formData.append('pad_ids', JSON.stringify(req.padIds));
  formData.append('destination_path', req.destinationPath || '/DCIM/');
  formData.append('auto_install', String(req.autoInstall || false));
  if (req.packageName) {
    formData.append('package_name', req.packageName);
  }

  const response = await fetch(`${BASE}/upload-file`, {
    method: 'POST',
    headers: {
      Authorization: `Bearer ${token}`,
    },
    body: formData,
  });

  if (!response.ok) {
    const error = await response.json().catch(() => ({ detail: response.statusText }));
    throw new Error(error.detail || `Upload failed: ${response.status}`);
  }

  return response.json();
}

export async function uploadFileToDevice(req: {
  file: File;
  padId: string;
  destinationPath?: string;
  autoInstall?: boolean;
  packageName?: string;
}): Promise<UploadFileResponse> {
  const token = await getAuthToken();
  if (!token) throw new Error('Not authenticated');

  const formData = new FormData();
  formData.append('file', req.file);
  formData.append('pad_ids', JSON.stringify([req.padId]));
  formData.append('destination_path', req.destinationPath || '/DCIM/');
  formData.append('auto_install', String(req.autoInstall || false));
  if (req.packageName) {
    formData.append('package_name', req.packageName);
  }

  const response = await fetch(`${BASE}/upload-file`, {
    method: 'POST',
    headers: {
      Authorization: `Bearer ${token}`,
    },
    body: formData,
  });

  if (!response.ok) {
    const error = await response.json().catch(() => ({ detail: response.statusText }));
    throw new Error(error.detail || `Upload failed: ${response.status}`);
  }

  return response.json();
}

// === Live stream (Web H5 SDK / armcloud-rtc) ===

export interface StreamToken {
  token: string;
  lock_token: string;
  base_url: string;
  pad_code: string;
  user_id: string;
}

export async function getStreamToken(padId: string): Promise<StreamToken> {
  const res = await authFetch(`${BASE}/devices/${padId}/stream-token`, {
    method: 'POST',
  });
  return res.json();
}

export async function releaseStreamToken(
  padId: string,
  payload: { token?: string; lock_token?: string },
): Promise<any> {
  const res = await authFetch(`${BASE}/devices/${padId}/stream-token/release`, {
    method: 'POST',
    body: JSON.stringify(payload),
  });
  return res.json();
}

export async function heartbeatStreamToken(
  padId: string,
  lockToken: string,
): Promise<{ refreshed: number }> {
  const res = await authFetch(`${BASE}/devices/${padId}/stream-token/heartbeat`, {
    method: 'POST',
    body: JSON.stringify({ lock_token: lockToken }),
  });
  return res.json();
}

export async function forceReleaseDeviceLock(padId: string): Promise<{ released: number }> {
  const res = await authFetch(`${BASE}/devices/${padId}/force-release`, {
    method: 'POST',
  });
  return res.json();
}

export async function sendDeviceKey(
  padId: string,
  keycode: number,
): Promise<{ results: Record<string, boolean> }> {
  const res = await authFetch(`${BASE}/devices/${padId}/key`, {
    method: 'POST',
    body: JSON.stringify({ keycode }),
  });
  return res.json();
}

/**
 * Fire-and-forget release for `pagehide`. Uses a cached bearer token and
 * `keepalive` so the request survives the page unloading.
 */
export function releaseStreamTokenKeepalive(
  padId: string,
  payload: { token?: string; lock_token?: string },
): void {
  const token = cachedAuthToken;
  if (!token) return;
  try {
    fetch(`${BASE}/devices/${padId}/stream-token/release`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${token}`,
      },
      body: JSON.stringify(payload),
      keepalive: true,
    }).catch(() => {});
  } catch {
    /* ignore */
  }
}
